import axios from 'axios'
import layoutHelpers from '@/layout/helpers.js'
import themeSettings from '@/vendor/libs/theme-settings/theme-settings.js'
import dotenv from 'dotenv'
import crypto from 'crypto'

dotenv.config()

export default function () {
  return {
    // Public url
    publicApiUrl: process.env.VUE_APP_BASE_API_URL,

    // Public url
    firebaseAuth: process.env.VUE_APP_FIREBASE ? layoutHelpers.getAuthObject(process.env.VUE_APP_FIREBASE) : null,

    youTubeApiKey: process.env.VUE_APP_YOUTUBE_API_KEY && process.env.VUE_APP_YOUTUBE_API_KEY !== 'undefined' ? process.env.VUE_APP_YOUTUBE_API_KEY : null,

    // Layout helpers
    layoutHelpers,

    // Theme settings
    themeSettings,

    // Check for RTL layout
    get isRTL () {
      return document.documentElement.getAttribute('dir') === 'rtl' || document.body.getAttribute('dir') === 'rtl'
    },

    // Check if IE
    get isIEMode () {
      return typeof document['documentMode'] === 'number'
    },

    // Check if IE10
    get isIE10Mode () {
      return this.isIEMode && document['documentMode'] === 10
    },

    // Layout navbar color
    get layoutNavbarBg () {
      return this.themeSettings.getOption('navbarBg') || 'navbar-theme'
    },

    // Layout sidenav color
    get layoutSidenavBg () {
      return this.themeSettings.getOption('sidenavBg') || 'sidenav-theme'
    },

    // Layout footer color
    get layoutFooterBg () {
      return this.themeSettings.getOption('footerBg') || 'footer-theme'
    },

    // Animate scrollTop
    scrollTop (to, duration, element = document.scrollingElement || document.documentElement) {
      if (element.scrollTop === to) return
      const start = element.scrollTop
      const change = to - start
      const startDate = +new Date()

      // t = current time; b = start value; c = change in value; d = duration
      const easeInOutQuad = (t, b, c, d) => {
        t /= d / 2
        if (t < 1) return c / 2 * t * t + b
        t--
        return -c / 2 * (t * (t - 2) - 1) + b
      }

      const animateScroll = () => {
        const currentDate = +new Date()
        const currentTime = currentDate - startDate
        element.scrollTop = parseInt(easeInOutQuad(currentTime, start, change, duration))
        if (currentTime < duration) {
          requestAnimationFrame(animateScroll)
        } else {
          element.scrollTop = to
        }
      }

      animateScroll()
    },

    // - CUSTOM KEYS

    /**
     * Generic function for http requests
     * using axios library
     * @param {object} config
     * @param {function} callback
     * @param {function} errorHandler
     * @return {void}
     */

    request: async (config, callback, errorHandler) => {
      try {
        const response = await axios(config)
        if (response.status >= 200 && response.status < 400) {
          callback(response.data)
        } else {
          errorHandler('Błąd przechwytywania odpowiedzi z zapytania.')
        }
      } catch (err) {
        var message = err.response && err.response.data && err.response.data.message ? err.response.data.message : err.message
        errorHandler(message)
      }
    },

    /**
     * Generic function for http requests
     * using axios library
     * @param {object} config
     * @return {void}
     */

    asyncRequest: (config) => {
      return axios(config)
    },

    /**
     * Form validator functions
     */

    invalidField (message) {
      return message
    },

    validField (message) {
      return message
    },

    validateField (field) {
      if (!field.touched) return true
      const status = field.condition(this)
      return !!status
    },

    setFieldTouched (field) {
      field.touched = true
    },

    checkIfValid (object) {
      let status = true
      Object.keys(object).forEach((key) => {
        const element = object[key]
        const { touched, required } = element
        if (!required && !touched) return
        this.setFieldTouched(element)
        const valid = this.validateField(element)
        if (!valid) {
          status = false
        }
      })
      return status
    },

    getFormErrors (object) {
      const errors = []
      Object.keys(object).forEach((key) => {
        const element = object[key]
        const { touched, required } = element
        if (!required && !touched) return
        this.setFieldTouched(element)
        const valid = this.validateField(element)
        if (!valid) {
          errors.push(`${key} - ${element.errorMessage || '---'}`)
        }
      })
      return errors
    },

    validateEmail (email) {
      const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return regex.test(String(email).toLowerCase())
    },

    /**
     * Parses a hostname from a URL or bare host.
     * Returns null if unparseable.
     */
    parsePortalHost (portalUrl) {
      if (!portalUrl || typeof portalUrl !== 'string') return null
      const candidate = portalUrl.trim()
      if (!candidate) return null
      try {
        const withScheme = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`
        return new URL(withScheme).hostname.toLowerCase()
      } catch (e) {
        return null
      }
    },

    /**
     * Normalizes a redirect URL.
     * - If full URL of this portal -> returns only path (+query+hash).
     * - If full URL of external domain -> returns unchanged.
     * - If relative path without leading slash -> prefixes '/'.
     */
    normalizeRedirectUrl (url, portalUrl) {
      if (typeof url !== 'string') return url
      const trimmed = url.trim()
      if (!trimmed) return trimmed

      if (/^https?:\/\//i.test(trimmed)) {
        try {
          const parsed = new URL(trimmed)
          const portalHost = this.parsePortalHost(portalUrl)
          if (portalHost && parsed.hostname.toLowerCase() === portalHost) {
            const pathOnly = parsed.pathname + (parsed.search || '') + (parsed.hash || '')
            return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
          }
          return trimmed
        } catch (e) {
          return trimmed
        }
      }

      return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    },

    /**
     * Validates a redirect URL and returns { valid, normalized, wasNormalized, message }.
     */
    validateRedirectUrl (url, portalUrl) {
      if (typeof url !== 'string' || url.trim() === '') {
        return { valid: false, normalized: '', wasNormalized: false, message: 'Pole jest wymagane' }
      }
      const normalized = this.normalizeRedirectUrl(url, portalUrl)
      const wasNormalized = normalized !== url
      return { valid: true, normalized, wasNormalized, message: '' }
    },

    /**
     * Prepares payload to send via axios
     * @param {any} object Initial object
     * @param {boolean} isFormData Response in FormData
     * @param stringifyObject
     * @returns {any} Formatted payload
     */

    preparePayload (object, isFormData = false, stringifyObject = false, ignoreKey = []) {
      const payload = isFormData ? new FormData() : {}
      Object.keys(object).forEach((key) => {
        const { value } = object[key]
        if (isFormData) {
          if (key === 'files') {
            value.forEach((file) => {
              payload.append(key, file)
            })
          } else {
            if (value || ignoreKey.includes(key)) {
              if (Array.isArray(value) && !ignoreKey.includes(key)) {
                payload.append(key, JSON.stringify(value))
              } else if (typeof value === 'object' && stringifyObject && !ignoreKey.includes(key)) {
                payload.append(key, JSON.stringify(value))
              } else {
                payload.append(key, value)
              }
            }
          }
        } else {
          payload[key] = value
        }
      })

      return payload
    },

    /**
     * Populating option key
     * for categories selection
     * @param {any[]} categories List of categories
     * @param {any} destination Object to return populated categories
     */

    populateOptions (categories, destination) {
      categories.forEach((item) => {
        destination.push({
          text: item.name,
          value: item.id
        })
      })
    },

    /**
     * Returns category ID based on category path
     * @param {string[]} path Category path
     * @param {any[]} categories Categories list
     * @returns {string|null} Category ID
     */

    getCategoryID (path, categories) {
      if (!path) {
        return null
      }
      const pathFormatted = path.join('.')
      const category = categories.find((item) => item.path.join('.') === pathFormatted)
      return category ? category.id : null
    },

    /**
     * Returns category ID based on category path
     * @param {string[]} path Category path
     * @param {any[]} categories Categories list
     * @returns {string|null} Category ID
     */

    getParentCategory (path, categories) {
      if (!path || path.length < 2) return null
      const parentCategoryValue = path[path.length - 2]
      const parentCategory = categories.find((item) => item.value === parentCategoryValue)
      return parentCategory ? parentCategory.id : null
    },

    /**
     * Populates generic form object
     * using data from API
     * @param {any} object Data from API
     * @param {any} form Initial form object
     */

    populateDataObject (object, form) {
      Object.keys(object).forEach((item) => {
        if (form.hasOwnProperty(item)) {
          form[item].value = object[item]
        }
      })
    },

    /**
     * Async foreach function
     * @param {any} array Data from API
     * @param {any} callback Initial form object
     */

    async asyncForEach (array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array)
      }
    },

    /**
   * Check if user is allowed to do operations
   * @param {array} array of allowed permissions
   */

    hasRole(roles) {
      let granted = false

      roles.forEach( (role) => {
        if (this.$store.state.user.permissions.includes(role)) {
          granted = true;
          return
        }
      })

      return granted;
    },

    levDist (s, t) {
      var d = []; //2d matrix

      var n = s.length;
      var m = t.length;

      if (n == 0) return m;
      if (m == 0) return n;

      for (var i = n; i >= 0; i--) d[i] = [];

      for (var i = n; i >= 0; i--) d[i][0] = i;
      for (var j = m; j >= 0; j--) d[0][j] = j;

      for (var i = 1; i <= n; i++) {
          var s_i = s.charAt(i - 1);

          for (var j = 1; j <= m; j++) {

              if (i == j && d[i][j] > 4) return n;

              var t_j = t.charAt(j - 1);
              var cost = (s_i == t_j) ? 0 : 1;

              var mi = d[i - 1][j] + 1;
              var b = d[i][j - 1] + 1;
              var c = d[i - 1][j - 1] + cost;

              if (b < mi) mi = b;
              if (c < mi) mi = c;

              d[i][j] = mi; // Step 6

              if (i > 1 && j > 1 && s_i == t.charAt(j - 2) && s.charAt(i - 2) == t_j) {
                  d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost);
              }
          }
      }

      // Step 7
      return d[n][m];
    },

    generateSlugFromString (string) {
      return string.toLowerCase() // Convert the string to lowercase letters
      .normalize('NFD') // The normalize() method returns the Unicode Normalization Form of a given string.
      .trim() // Remove whitespace from both sides of a string
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[\ł]+/g, 'l') // Replaces ł with l
      .replace(/[^\w\-]+/g, '') // Remove all non-word chars
      .replace(/\-\-+/g, '-'); // Replace multiple - with single -
    },

    getToken () {
      const date = new Date();

      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return crypto.createHash('md5').update(`Admin.Multiportal.MaBmh0FvdW.${year}.${month}.${day}`).digest('hex')
    },

    generateUid (length = 20) {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const array = new Uint8Array(length);
      window.crypto.getRandomValues(array);

      let result = '';
      for (let i = 0; i < length; i++) {
        const randomIndex = array[i] % characters.length;
        result += characters.charAt(randomIndex);
      }
      return result;
    }

  }
}
